// Drill import utilities
import { Drill } from '@/components/drill-library/types/drill-types';
import { parseCSVFile, DrillImportData } from './drill-import-csv';

export type ImportMode = 'skip' | 'reimport';

// Re-export DrillImportData for backward compatibility
export type { DrillImportData };

/**
 * Validate and parse import file (CSV or JSON)
 * Detects file format and calls appropriate parser
 */
export const validateImportFile = async (content: string, fileName?: string): Promise<DrillImportData> => {
  // Detect format by file extension or content
  const isCSV = fileName?.toLowerCase().endsWith('.csv') || 
                content.trim().startsWith('Title') || 
                content.includes(',') && !content.trim().startsWith('[') && !content.trim().startsWith('{');
  
  if (isCSV) {
    // Parse CSV file
    return parseCSVFile(content);
  } else {
    // Parse JSON file (backward compatibility)
    try {
      const data = JSON.parse(content);
      
      // Handle both formats:
      // 1. Array of drills directly (exported format)
      // 2. Object with drills property (wrapper format)
      
      if (Array.isArray(data)) {
        // Direct array of drills
        return {
          drills: data,
          duplicateCount: 0,
          exportedAt: undefined,
          version: undefined
        };
      } else if (data.drills && Array.isArray(data.drills)) {
        // Object with drills property
        return {
          drills: data.drills,
          duplicateCount: 0,
          exportedAt: data.exportedAt,
          version: data.version
        };
      }
      
      throw new Error('Invalid file format - expected an array of drills or an object with a drills property');
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error('Invalid JSON file - the file contains syntax errors');
      }
      throw error instanceof Error ? error : new Error('Invalid JSON file');
    }
  }
};

export const readFileAsText = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
};

// Extract the YouTube video ID from a URL string.
// The primary source of truth is the "v" query parameter.
// Falls back to common URL patterns if needed.
export const extractYouTubeVideoId = (urlString?: string | null): string | null => {
  if (!urlString || typeof urlString !== 'string' || urlString.trim().length === 0) return null;
  
  // Basic validation: URL should start with http:// or https://
  const trimmedUrl = urlString.trim();
  if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
    // Try to parse a raw v= query as fallback
    const match = /[?&]v=([^&\s]+)/.exec(trimmedUrl);
    if (match && match[1]) return match[1].trim();
    return null;
  }
  
  try {
    const url = new URL(trimmedUrl);

    // Prefer explicit "v" parameter (canonical case)
    const vParam = url.searchParams.get('v');
    if (vParam && vParam.trim().length > 0) return vParam.trim();

    // Handle youtu.be short links: https://youtu.be/<id>
    if (url.hostname.endsWith('youtu.be')) {
      const idFromPath = url.pathname.replace(/^\//, '').trim();
      return idFromPath.length > 0 ? idFromPath : null;
    }

    // Handle embed and shorts paths just in case
    // e.g. /embed/<id>, /shorts/<id>
    const pathParts = url.pathname.split('/').filter(Boolean);
    if (pathParts.length >= 2) {
      const [first, second] = pathParts;
      if ((first === 'embed' || first === 'shorts') && second) {
        return second.trim();
      }
    }
  } catch {
    // If it's not a valid URL, best effort: try to parse a raw v= query
    const match = /[?&]v=([^&\s]+)/.exec(urlString);
    if (match && match[1]) return match[1].trim();
  }
  return null;
};

// Check if two drills are duplicates based ONLY on YouTube video ID
export const isDuplicateDrill = (drill1: Drill, drill2: Drill, _similarityThreshold: number = 0.8): boolean => {
  const id1 = extractYouTubeVideoId(drill1?.videoUrl ?? null);
  const id2 = extractYouTubeVideoId(drill2?.videoUrl ?? null);
  if (!id1 || !id2) return false;
  return id1 === id2;
};

export const importDrills = async (
  data: DrillImportData,
  mode: ImportMode,
  existingDrills: Drill[],
  deleteAllDrills?: () => Promise<void>
): Promise<{ success: number; errors: string[] }> => {
  const errors: string[] = [];
  let successCount = 0;
  
  try {
    // Handle reimport mode - delete all existing drills first
    if (mode === 'reimport' && deleteAllDrills) {
      await deleteAllDrills();
    }
    
    // Process each drill based on the import mode
    for (const drill of data.drills) {
      try {
        // Check for duplicates (except in reimport mode)
        if (mode !== 'reimport') {
          const isDuplicate = existingDrills.some(existing => 
            isDuplicateDrill(drill, existing)
          );
          
          if (isDuplicate && mode === 'skip') {
            continue; // Skip duplicates
          }
        }
        
        // Add the drill (implementation would depend on your Firebase service)
        // This is a placeholder - you'll need to implement the actual drill addition
        successCount++;
        
      } catch (err) {
        const errorMsg = `Failed to import "${drill.name}": ${err instanceof Error ? err.message : 'Unknown error'}`;
        errors.push(errorMsg);
      }
    }
    
  } catch (err) {
    errors.push(`Import failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
  }
  
  return { success: successCount, errors };
};
